import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "@/lib/scroll";
import { perBagMoney, useBestPerBag } from "@/lib/shop";
import { HeroBags } from "./HeroBags";

const LINES = [["Candy with", ""], ["a little secret:", "text-[#FF5A5F] italic"], ["22g protein.", ""]];
const CHIPS = ["22g protein", "75% less sugar", "7g fibre", "Sweetened with honey"];

const MaskLine = ({ text, cls, i }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span className={`block ${cls}`} initial={{ y: "105%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}>{text}</motion.span>
  </span>
);

const fade = (d) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.8, ease: EASE } });

export const Hero = () => {
  const ref = useRef(null);
  const perBag = useBestPerBag();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 80, damping: 15 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} data-testid="hero-section" onMouseMove={onMove} className="relative pt-[124px] sm:pt-36 pb-12 md:min-h-[100svh] overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 grid md:grid-cols-12 gap-x-8 md:content-center">
        <div className="md:col-span-6 md:row-start-1 relative z-10 md:self-end">
          <motion.p {...fade(0.05)} className="eyebrow text-[#FF5A5F] mb-3 sm:mb-5">Protein gummies · Made in Nova Scotia</motion.p>
          <h1 data-testid="hero-title-masked" className="font-display leading-[0.92] text-[11.5vw] sm:text-[9vw] md:text-[3.2rem] lg:text-[5.6rem] xl:text-[6.4rem]">
            {LINES.map(([t, c], i) => <MaskLine key={t} text={t} cls={c} i={i} />)}
          </h1>
        </div>
        <HeroBags rx={rx} ry={ry} progress={scrollYProgress} />
        <div className="md:col-span-6 md:row-start-2 relative z-10 md:self-start">
          <motion.p {...fade(0.7)} className="mt-2 md:mt-6 max-w-xl text-base sm:text-lg text-[#4A4560] leading-relaxed">
            Soft, fruity gummy bears sweetened with honey. An afternoon pick-me-up, a post-workout treat, a road-trip snack you'll actually look forward to. Made by Rory, who started at 14 with $300.
          </motion.p>
          <motion.div {...fade(0.85)} className="mt-6 flex flex-wrap gap-2">
            {CHIPS.map((c, i) => <span key={c} data-testid={`hero-macro-badge-${i}`} className="rounded-full bg-white border border-[#1B1530]/10 px-3.5 py-1.5 text-sm font-semibold">{c}</span>)}
          </motion.div>
          <motion.div {...fade(1)} className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/shop" data-testid="hero-shop-cta" className="group inline-flex items-center gap-3 rounded-full bg-[#FF5A5F] text-white h-14 pl-7 pr-2 font-bold hover:bg-[#1B1530] transition-colors">
              {`Shop now${perBag ? ` · from ${perBagMoney(perBag)}/bag` : ""}`}
              <span className="h-10 w-10 rounded-full bg-white text-[#1B1530] grid place-items-center transition-transform duration-300 group-hover:translate-x-1"><ArrowRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/story" data-testid="hero-story-button" className="h-14 px-6 grid place-items-center rounded-full border-2 border-[#1B1530] font-bold hover:bg-[#1B1530] hover:text-white transition-colors">Meet Rory</Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
